import { CircularProgress } from "@material-ui/core";
import axios from 'axios';
import React, { Fragment, useEffect } from "react";

import { File as MyFile } from "../../../backend/generated/file-model";
import { SignedUrl } from "../../../backend/generated/signed-url-model";
import { convertToKey } from "../../../backend/types/serialization";
import { MimeTypes } from "../../../backend/types/utils";
import { GenerateUploadSignedURL } from "../../../graphql/queryDescriptors";
import { FileManageable, GenerateSignedUrlManageable } from "../../../store/file/types";
import { useCustomQuery } from "../../../store/hooks/useCustomQuery";
import { ActionButton } from "../../ActionButton";
import { Button } from ".";
import { ButtonProps } from "./Button";

export type AcceptedFileType = MyFile | File;

type Omitted =
  | 'value'
  | 'variant'

export interface FileUploadProps extends Omit<ButtonProps, Omitted> {
  buttonValue: any;
  onFilesSelected?: (value: Array<AcceptedFileType>) => void;
  onUploadEnded?: (value: Array<AcceptedFileType>) => void;
  stateValue: any;
  value: Array<AcceptedFileType>;
  variant: 'icon' | undefined;
}

export const FileUpload = (props: FileUploadProps) => {
  // Props
  const {
    buttonValue,
    className,
    onFilesSelected,
    onUploadEnded,
    startIcon,
    stateValue,
    value,
    variant,
  } = props;

  const buttonProps = { ...props } as any;
  delete buttonProps.buttonValue;
  delete buttonProps.onFilesSelected;
  delete buttonProps.onUploadEnded;
  delete buttonProps.stateValue;
  delete buttonProps.value;
  delete buttonProps.variant;

  // Hooks
  const hiddenFileInput = React.useRef<HTMLInputElement>(null);

  const {
    execute: generateUploadSignedURL, loading: generateSignedUploadURLLoading
  } = useCustomQuery(GenerateUploadSignedURL({
    onSuccess: async (data: Array<SignedUrl>) => {
      const alreadyUploadedFiles = value.filter((file: any) => !!file.id);

      const newUploadedFiles = [];

      for (let i = 0; i < data.length; i++) {
        const url = data[i].url;
        const allFilesIndex = alreadyUploadedFiles.length + i;
        const file = value[allFilesIndex] as any;
        const uploadResult = await axios.put(
          url,
          file,
          { headers: { 'Content-Type': file.type || 'text/plain' } }
        );

        newUploadedFiles.push(new FileManageable({
          id: data[i].fileId,
          url: uploadResult.config.url?.split(
            "?",
            2
          )[0] || "",
          isFeatured: allFilesIndex === 0,
          mimeType: convertToKey(
            MimeTypes,
            file.type
          ),
          name: file.name,
        }))
      }

      const allUploadedFiles = alreadyUploadedFiles.concat(newUploadedFiles);

      if (onUploadEnded) {
        onUploadEnded(allUploadedFiles);
      }
    },
  }));

  const empty = value.length === 0;

  useEffect(
    () => {
      if (!empty) {
        const notUploadedFiles = value.filter((file: any) => !file.id);
        if (notUploadedFiles.length > 0) {
          generateUploadSignedURL({ variables: { input: new GenerateSignedUrlManageable({ mimeTypes: notUploadedFiles.map((file: any) => file.type) }).serialize() } });
        }
      }
    },
    [value]
  )

  // Helpers
  const handleClick = () => {
    if (hiddenFileInput !== null &&
        hiddenFileInput.current !== null) {
      hiddenFileInput.current.click();
    }
  };

  const allowedTypes = Object.values(MimeTypes);

  return (
    <Fragment>
      {variant === "icon" &&
      <ActionButton
        className={className}
        onClick={handleClick}
        icon={startIcon}
        disabled={generateSignedUploadURLLoading}
      />
      ||
      (generateSignedUploadURLLoading ?
        <CircularProgress />
        :
        <Button
          onClick={handleClick}
          {...buttonProps}
          value={empty ? buttonValue : value.length + ' ' + stateValue}
          variant={undefined}
        />
      )}

      <input
        type="file"
        accept={allowedTypes.join(',')}
        ref={hiddenFileInput}
        style={{ display: 'none' }}
        multiple
        required
        onChange={({
          target: {
            validity, files: newFiles
          }
        }) => {
          if (validity.valid && newFiles) {
            let allFiles = [...value];
            let newFilesFiltered = Array.from(newFiles).filter((file: any) => allowedTypes.includes(file.type));
            allFiles = allFiles.concat(newFilesFiltered);
            if (onFilesSelected) {
              onFilesSelected(allFiles);
            }
          }
        }}
      />
    </Fragment>
  );
}